import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle, AlertTriangle, MapPin, Users, Briefcase,
  Car, Clock, Globe, Star, ChevronRight, Lock, Eye, EyeOff,
  ArrowRight, Building2, Handshake, TrendingUp
} from 'lucide-react';

const PASSWORD = 'YGO-VTC-2026';
const SESSION_KEY = 'subcontractors_auth';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Default pricing (fallback if API unreachable) ─────────────────────────────

const DEFAULT_LONG_DISTANCE = [
  { destination: 'Rouen', berline: 195, van: 350 },
  { destination: 'Dieppe', berline: 250, van: 350 },
  { destination: 'Le Havre', berline: 250, van: 350 },
  { destination: 'Caen', berline: 300, van: 420 },
  { destination: 'Cherbourg', berline: 400, van: 560 },
  { destination: 'Saint-Malo', berline: 470, van: 700 },
  { destination: 'Dunkerque', berline: 300, van: 420 },
];

const DEFAULT_PARIS_REGION = [
  { destination: 'Campanile (CDG)', berline: 35, van: 50 },
  { destination: 'Gare Montparnasse', berline: 75, van: 120 },
  { destination: 'Gare du Nord', berline: 60, van: 90 },
  { destination: "Gare de l'Est", berline: 60, van: 90 },
  { destination: 'Gare de Lyon', berline: 70, van: 100 },
  { destination: 'Gare Saint-Lazare', berline: 70, van: 120 },
  { destination: 'Orly', berline: 90, van: 120 },
];

const REQUIREMENTS = [
  'VTC indépendant (auto-entrepreneur ou société)',
  'Société immatriculée et active (KBIS à jour)',
  'Conformité administrative complète',
  'Véhicule personnel propre et entretenu',
  'Présentation irréprochable du véhicule',
  'Tenue professionnelle obligatoire',
  'Conduite sécuritaire, respect du code',
  'Anglais courant (clientèle internationale)',
  'Ponctualité stricte sans exception',
];

const OPERATIONAL = [
  'Accueil pancarte systématique à l\'arrivée',
  'Parking professionnel aéroport requis',
  'Inscription sur notre plateforme interne',
  'Localisation activée pendant la mission uniquement',
  'Respect strict des procédures Y GO',
  'Reporting immédiat en cas d\'incident',
];

const STEPS = [
  { num: '01', label: 'Présélection', desc: 'Examen de votre dossier et de votre profil' },
  { num: '02', label: 'Validation documents', desc: 'Vérification administrative et véhicule' },
  { num: '03', label: 'Acceptation conditions', desc: 'Signature des engagements contractuels' },
  { num: '04', label: 'Intégration', desc: 'Accès au groupe opérationnel et premières missions' },
];

// ── Password Gate ─────────────────────────────────────────────────────────────

function PasswordGate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value === PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setValue('');
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/ygo-logo.png" alt="Y GO" className="h-12 mb-4 opacity-90" />
          <p className="text-[#C9A24D] text-xs tracking-[0.3em] uppercase font-medium">
            Espace partenaires
          </p>
        </div>

        {/* Card */}
        <div
          className={`bg-[#0f0f0f] border border-white/8 rounded-2xl p-8 transition-all duration-200 ${shake ? 'animate-[shake_0.4s_ease]' : ''}`}
          style={shake ? { animation: 'shake 0.4s ease' } : {}}
        >
          <div className="flex justify-center mb-6">
            <div className="w-12 h-12 rounded-full bg-[#C9A24D]/10 border border-[#C9A24D]/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#C9A24D]" />
            </div>
          </div>
          <h2 className="text-white text-center text-base font-semibold mb-1">Accès sécurisé</h2>
          <p className="text-white/40 text-xs text-center mb-6">Saisissez le code d'accès pour consulter les conditions de partenariat</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <input
                type={visible ? 'text' : 'password'}
                value={value}
                onChange={e => { setValue(e.target.value); setError(false); }}
                placeholder="Code d'accès"
                autoFocus
                className={`w-full bg-white/5 border ${error ? 'border-red-500/60' : 'border-white/10'} rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#C9A24D]/60 transition-colors pr-11`}
              />
              <button
                type="button"
                onClick={() => setVisible(!visible)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
              >
                {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {error && (
              <p className="text-red-400 text-xs flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Code incorrect. Vérifiez et réessayez.
              </p>
            )}
            <button
              type="submit"
              className="w-full bg-[#C9A24D] hover:bg-[#b8913f] active:scale-[0.98] text-white font-semibold text-sm py-3 rounded-xl transition-all duration-150 flex items-center justify-center gap-2"
            >
              Accéder
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <p className="text-white/20 text-xs text-center mt-6">
          © {new Date().getFullYear()} Y GO Shuttle Services France
        </p>
      </div>

      <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}

// ── Pricing Table ─────────────────────────────────────────────────────────────

function PricingTable({ title, rows, icon: Icon }) {
  return (
    <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-white/8 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#C9A24D]/15 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#C9A24D]" />
        </div>
        <h3 className="text-white font-semibold text-sm">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left px-6 py-3 text-white/40 text-xs font-medium uppercase tracking-wider">Destination</th>
              <th className="text-right px-4 py-3 text-white/40 text-xs font-medium uppercase tracking-wider">Berline</th>
              <th className="text-right px-6 py-3 text-white/40 text-xs font-medium uppercase tracking-wider">Van</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-white/4 last:border-0 hover:bg-white/2 transition-colors">
                <td className="px-6 py-3.5 text-white text-sm font-medium">{row.destination}</td>
                <td className="px-4 py-3.5 text-right">
                  <span className="text-[#C9A24D] font-semibold text-sm">{row.berline} €</span>
                  <span className="text-white/30 text-xs ml-1">HT</span>
                </td>
                <td className="px-6 py-3.5 text-right">
                  <span className="text-[#C9A24D] font-semibold text-sm">{row.van} €</span>
                  <span className="text-white/30 text-xs ml-1">HT</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

function SubcontractorsPage() {
  const navigate = useNavigate();
  const [rates, setRates] = useState({
    commission_percent: 10,
    long_distance: DEFAULT_LONG_DISTANCE,
    paris_region: DEFAULT_PARIS_REGION,
  });

  useEffect(() => {
    fetch(`${API_URL}/api/public/vtc-rates`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && Array.isArray(d.long_distance) && Array.isArray(d.paris_region)) {
          setRates({
            commission_percent: d.commission_percent ?? 10,
            long_distance: d.long_distance.length ? d.long_distance : DEFAULT_LONG_DISTANCE,
            paris_region: d.paris_region.length ? d.paris_region : DEFAULT_PARIS_REGION,
          });
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  return (
    <div className="min-h-screen bg-[#080808] text-white">

      {/* ── HERO ── */}
      <div className="relative overflow-hidden border-b border-white/8 min-h-[520px] flex items-stretch">
        {/* Background grain */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#C9A24D]/4 via-transparent to-transparent" />

        {/* Hero image — right half, fading to the left */}
        <div className="absolute inset-y-0 right-0 w-full lg:w-3/5 overflow-hidden pointer-events-none">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/8c31b2bf-c860-4998-9ed7-6ccae7ba631d/images/a37534468366e6983dbcbd25058d4c2e3862273647297a23cdec0ab464afbe01.png"
            alt="Véhicule VTC Y GO sur le quai d'un port maritime"
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.75) saturate(0.9)' }}
          />
          {/* Gradient overlay: solid dark on left, transparent on right */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #080808 0%, #080808 30%, rgba(8,8,8,0.7) 55%, rgba(8,8,8,0.15) 100%)' }} />
          {/* Bottom fade */}
          <div className="absolute bottom-0 inset-x-0 h-24" style={{ background: 'linear-gradient(to top, #080808, transparent)' }} />
        </div>

        <div className="relative w-full max-w-5xl mx-auto px-6 py-20 md:py-28 lg:w-1/2">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-[#C9A24D] text-xs tracking-[0.3em] uppercase font-medium">Y GO Shuttle Services France</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Sous-traitance VTC<br />
            <span className="text-[#C9A24D]">Transferts Maritimes</span><br />
            <span className="text-white/70">&amp; Croisières</span>
          </h1>
          <p className="text-white/50 text-base sm:text-lg max-w-2xl leading-relaxed mb-10">
            Collaborez avec nous sur des missions longue distance pour équipages internationaux et croisiéristes.
            Contrats directs avec agences maritimes. Volume conséquent et régulier.
          </p>

          {/* Stats bar */}
          <div className="flex flex-wrap gap-4">
            {[
              { icon: Handshake, label: 'Contrats directs', sub: 'Agences maritimes & opérateurs croisière' },
              { icon: TrendingUp, label: 'Volume régulier', sub: 'Sélection limitée de partenaires' },
              { icon: Shield, label: '10 % commission', sub: 'Transparente et fixe' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex items-start gap-3 bg-white/4 border border-white/8 rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-[#C9A24D]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-[#C9A24D]" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{label}</p>
                  <p className="text-white/40 text-xs">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16 space-y-20">

        {/* ── SECTION 1 : Présentation ── */}
        <section>
          <SectionLabel number="01" label="Présentation du partenariat" />
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[#C9A24D]" /> Notre activité
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Y GO assure des transferts VTC dans le cadre de <strong className="text-white">contrats directs avec des agences maritimes
                et des opérateurs croisière</strong>. Nous ne sommes pas une plateforme type Uber ou Bolt.
              </p>
            </div>
            <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#C9A24D]" /> Types de clients
              </h3>
              <ul className="text-white/60 text-sm space-y-1.5">
                {['Marins internationaux (Philippines, Birmanie, Ukraine, Russie, Inde, Indonésie, UK)', 'Croisiéristes (principalement américains et mexicains)'].map(item => (
                  <li key={item} className="flex items-start gap-2">
                    <ChevronRight className="w-3.5 h-3.5 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── SECTION 2 : Missions ── */}
        <section>
          <SectionLabel number="02" label="Nature des missions" />
          <div className="mt-8 bg-[#0f0f0f] border border-white/8 rounded-2xl p-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-3 font-medium">Point de départ</p>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#C9A24D]/15 border border-[#C9A24D]/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-[#C9A24D]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Aéroport Charles de Gaulle</p>
                    <p className="text-white/40 text-xs">Terminal 1, 2 & 3</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-3 font-medium">Destinations</p>
                <div className="flex flex-wrap gap-2">
                  {['Le Havre', 'Caen', 'Dunkerque', 'Rouen', 'Dieppe', 'Cherbourg', 'Saint-Malo', 'Nantes*', 'La Rochelle*', 'Hôtels région Paris'].map(d => (
                    <span key={d} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${d.includes('*') ? 'bg-white/3 border-white/8 text-white/40' : 'bg-[#C9A24D]/10 border-[#C9A24D]/25 text-[#C9A24D]'}`}>
                      {d}
                    </span>
                  ))}
                </div>
                <p className="text-white/25 text-xs mt-2">* Occasionnellement</p>
              </div>
            </div>
          </div>

          {/* Autres types de missions */}
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Briefcase,
                title: 'Transferts équipages',
                desc: 'Mission principale et régulière. Prise en charge de membres d\'équipage internationaux à CDG vers les ports normands et la région parisienne.',
                tag: 'Mission régulière',
                highlight: true,
              },
              {
                icon: Star,
                title: 'Transferts VIP',
                desc: 'Prise en charge haut de gamme de clients VIP avec accueil soigné, assistance bagages et ponctualité absolue.',
                tag: 'Moins fréquent',
                highlight: false,
              },
              {
                icon: Users,
                title: 'Transferts croisiéristes',
                desc: 'Acheminement de groupes de croisiéristes entre CDG et les ports normands (Le Havre, Caen, Dunkerque…).',
                tag: 'Moins fréquent',
                highlight: false,
              },
              {
                icon: Globe,
                title: 'Excursions journée complète',
                desc: 'Circuits sur Paris ou en Normandie au départ de Paris — Mont-Saint-Michel, Bayeux, plages du Débarquement, Étretat.',
                tag: 'Moins fréquent',
                highlight: false,
              },
            ].map(({ icon: Icon, title, desc, tag, highlight }) => (
              <div key={title} className={`rounded-2xl p-5 border ${highlight ? 'bg-[#C9A24D]/6 border-[#C9A24D]/25' : 'bg-[#0f0f0f] border-white/8'}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-[#C9A24D]/20 border border-[#C9A24D]/40' : 'bg-[#C9A24D]/10 border border-[#C9A24D]/20'}`}>
                    <Icon className="w-4 h-4 text-[#C9A24D]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{title}</p>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-md text-[10px] font-medium border ${highlight ? 'bg-[#C9A24D]/15 border-[#C9A24D]/35 text-[#C9A24D]' : 'bg-white/4 border-white/10 text-white/35'}`}>{tag}</span>
                  </div>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── SECTION 3 : Tarifs ── */}
        <section>
          <SectionLabel number="03" label="Grille tarifaire" />
          <div className="mt-4 mb-6 flex items-start gap-2.5 bg-amber-500/8 border border-amber-500/20 rounded-xl px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300/80 text-xs leading-relaxed">
              <strong className="text-amber-300">Tarifs imposés et non négociables.</strong> Tous les prix sont exprimés <strong className="text-amber-300">Hors Taxes (HT)</strong>.
              Une commission de {rates.commission_percent}% est appliquée sur ces montants.
            </p>
          </div>
          <div className="space-y-4 mt-6">
            <PricingTable title="Longue distance — Équipages maritimes & Ports" rows={rates.long_distance} icon={MapPin} />
            <PricingTable title="Région parisienne — Hôtels & Gares" rows={rates.paris_region} icon={Building2} />
          </div>
        </section>

        {/* ── SECTION 4 : Commission ── */}
        <section>
          <SectionLabel number="04" label="Commission" />
          <div className="mt-8 bg-gradient-to-br from-[#C9A24D]/8 via-[#0f0f0f] to-[#0f0f0f] border border-[#C9A24D]/20 rounded-2xl p-8">
            <div className="flex items-start gap-6">
              <div className="text-5xl font-black text-[#C9A24D] leading-none flex-shrink-0">{rates.commission_percent}%</div>
              <div>
                <p className="text-white font-semibold mb-4">Commission appliquée sur chaque course <span className="text-white/40 font-normal text-sm">— fixe et transparente</span></p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    'Apport d\'affaires',
                    'Gestion contractuelle',
                    'Suivi opérationnel',
                    'Coordination avec les agences maritimes',
                    'Support administratif',
                  ].map(item => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-[#C9A24D] flex-shrink-0" />
                      <span className="text-white/60 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 5 : Exigences ── */}
        <section>
          <SectionLabel number="05" label="Exigences obligatoires" />
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-6">
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#C9A24D]" /> Profil & statut
              </h3>
              <ul className="space-y-2.5">
                {REQUIREMENTS.map(item => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckCircle className="w-3.5 h-3.5 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-6">
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                <Car className="w-4 h-4 text-[#C9A24D]" /> Exigences opérationnelles
              </h3>
              <ul className="space-y-2.5">
                {OPERATIONAL.map(item => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckCircle className="w-3.5 h-3.5 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                    <span className="text-white/60 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-white/8 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-300/60 text-xs">Aucun suivi hors mission. La localisation est désactivée en dehors des périodes actives.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 6 : Philosophie ── */}
        <section>
          <SectionLabel number="06" label="Notre philosophie de sélection" />
          <div className="mt-8 bg-[#0f0f0f] border border-white/8 rounded-2xl p-8">
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              Nous ne cherchons <strong className="text-white">pas à multiplier les chauffeurs</strong>. Notre modèle repose sur
              un nombre <strong className="text-white">délibérément limité</strong> de profils sélectionnés pour garantir un
              volume conséquent et régulier à chacun.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: Users, title: 'Sélection limitée', desc: 'Peu de partenaires, davantage de missions par profil' },
                { icon: TrendingUp, title: 'Volume garanti', desc: 'Missions régulières tout au long de l\'année' },
                { icon: Star, title: 'Standard élevé', desc: 'Qualité constante exigée, engagement réciproque' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex flex-col items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#C9A24D]/10 border border-[#C9A24D]/20 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#C9A24D]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{title}</p>
                    <p className="text-white/40 text-xs mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 7 : Processus ── */}
        <section>
          <SectionLabel number="07" label="Processus de validation" />
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((step, i) => (
              <div key={step.num} className="relative">
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-5 left-full w-full h-px bg-white/8 z-0" style={{ width: 'calc(100% - 2rem)', left: '50%' }} />
                )}
                <div className="bg-[#0f0f0f] border border-white/8 rounded-2xl p-5 relative z-10">
                  <div className="text-[#C9A24D] text-xs font-bold tracking-widest mb-3 font-mono">{step.num}</div>
                  <p className="text-white font-semibold text-sm mb-1.5">{step.label}</p>
                  <p className="text-white/40 text-xs leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="text-center py-8">
          <div className="bg-gradient-to-b from-[#C9A24D]/6 to-transparent border border-[#C9A24D]/15 rounded-3xl p-12">
            <div className="w-14 h-14 rounded-2xl bg-[#C9A24D]/15 border border-[#C9A24D]/30 flex items-center justify-center mx-auto mb-6">
              <Globe className="w-7 h-7 text-[#C9A24D]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Prêt à rejoindre le réseau Y GO ?</h2>
            <p className="text-white/45 text-sm max-w-md mx-auto mb-8 leading-relaxed">
              Si votre profil correspond à nos exigences, commencez dès maintenant le processus de validation
              en créant votre compte chauffeur partenaire.
            </p>
            <button
              data-testid="subcontractors-cta-button"
              onClick={() => navigate('/driver/register')}
              className="inline-flex items-center gap-3 bg-[#C9A24D] hover:bg-[#b8913f] active:scale-[0.98] text-white font-semibold px-8 py-4 rounded-2xl transition-all duration-150 text-sm shadow-lg shadow-[#C9A24D]/15 group"
            >
              Je souhaite intégrer le process de validation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-white/25 text-xs mt-5 flex items-center justify-center gap-1.5">
              <Clock className="w-3 h-3" />
              Réponse sous 48–72h ouvrées après soumission du dossier
            </p>
          </div>
        </section>

      </div>

      {/* Footer */}
      <div className="border-t border-white/8 py-6">
        <p className="text-center text-white/20 text-xs">
          © {new Date().getFullYear()} Y GO Shuttle Services France — Document confidentiel réservé aux partenaires
        </p>
      </div>
    </div>
  );
}

// ── Section Label helper ───────────────────────────────────────────────────────

function SectionLabel({ number, label }) {
  return (
    <div className="flex items-center gap-4">
      <span className="text-[#C9A24D] font-mono text-xs font-bold tracking-widest opacity-60">{number}</span>
      <div className="h-px flex-1 bg-white/8" />
      <h2 className="text-white font-bold text-base sm:text-lg">{label}</h2>
    </div>
  );
}

// ── Root component ─────────────────────────────────────────────────────────────

export default function SubcontractorsRatesPage() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === '1') {
      setUnlocked(true);
    }
  }, []);

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  return <SubcontractorsPage />;
}
